import React, { useState, useEffect, useMemo, useCallback, useDeferredValue, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    InputAdornment,
    Box,
    Typography,
    Chip,
    Stack,
    Card,
    Avatar,
    Checkbox,
    Radio,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Badge,
    Skeleton,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    Search as SearchIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    SwapVert as SwapVertIcon
} from '@mui/icons-material';
import { initializeDB } from '../../config/db';
import useProgressiveList from '../../hooks/useProgressiveList';
import SelectedStrip from '../shared/SelectedStrip';
import { EMPLOYEE_STORE, TEMPS_STORE } from '../../config/constants';

const PersonCard = React.memo(({ person, isSelected, onToggle, singleSelect, hideTypeChip }) => {
    const isEmployee = person._type === 'employee';

    return (
        <Card
            sx={{
                p: 1.5,
                cursor: 'pointer',
                backgroundColor: isSelected ? '#e3f2fd' : 'white',
                border: isSelected ? '2px solid #2196f3' : '1px solid #e0e0e0',
                '&:hover': {
                    backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5'
                }
            }}
            onClick={() => onToggle(person)}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {singleSelect ? (
                    <Radio checked={isSelected} sx={{ p: 0 }} />
                ) : (
                    <Checkbox checked={isSelected} sx={{ p: 0 }} />
                )}
                <Avatar sx={{
                    backgroundColor: isEmployee ? '#2196f3' : '#ff9800',
                    width: 36,
                    height: 36
                }}>
                    <PersonIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="500">
                        {person.FullName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: 14, color: '#666' }} />
                        <Typography variant="caption" color="text.secondary">
                            {isEmployee
                                ? (person.Division || 'N/A')
                                : (person.Division ? `Div ${person.Division} · ${person.Agency}` : (person.Agency || 'N/A'))}
                        </Typography>
                    </Box>
                </Box>
                {!hideTypeChip && (
                    <Chip
                        label={isEmployee ? 'Employee' : 'Temp'}
                        size="small"
                        sx={{
                            backgroundColor: isEmployee ? '#e3f2fd' : '#fff3e0',
                            color: isEmployee ? '#1976d2' : '#f57c00',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20
                        }}
                    />
                )}
            </Box>
        </Card>
    );
});

PersonCard.displayName = 'PersonCard';

/**
 * Вошедший среди людей списка: по EE# (localStorage employeeId), запасной
 * вариант — по ФИО. Так же ищет его timesheetService.findSelfEmployee.
 */
function findSelfIndex(people) {
    const ee = (localStorage.getItem('employeeId') || '').trim();
    const name = (localStorage.getItem('employeeFullName') || '').trim().toLowerCase();
    const isEmp = p => p._type === 'employee';
    let idx = ee ? people.findIndex(p => isEmp(p) && String(p.employeeId ?? '').trim() === ee) : -1;
    if (idx < 0 && name) {
        idx = people.findIndex(p => isEmp(p) && (p.FullName || '').trim().toLowerCase() === name);
    }
    return idx;
}

export default function SelectEmployeesAndTempsModal({
    open,
    onClose,
    onSelect,
    alreadySelected = [],
    singleSelect = false,
    employeesOnly = false,
    enableDivisionFilter = false,
    divisionFilterStorageKey = 'selectEmployeesDivisionFilter',
    // Полоса выбранных над списком: видно, кто уже отмечен, пока ищешь
    // следующих (таймшит). Остальные формы живут без неё, как раньше.
    showSelectedStrip = false,
    // Вошедший — первым в списке (таймшит): себя обычно добавляют первым.
    pinSelfFirst = false
}) {
    const [employees, setEmployees] = useState([]);
    const [temps, setTemps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    // Поле ввода отвечает сразу, список догоняет — набор не ждёт перерисовки карточек.
    const deferredSearch = useDeferredValue(searchTerm);
    const searchInputRef = useRef(null);
    const searchTermRef = useRef(searchTerm);
    searchTermRef.current = searchTerm;
    const [filterType, setFilterType] = useState('all');
    const [selected, setSelected] = useState([]);
    // 'default' = order from IDB, 'asc' = A→Z, 'desc' = Z→A
    const [sortOrder, setSortOrder] = useState('default');

    const userDivision = useMemo(
        () => (localStorage.getItem('employeeDivision') || '').trim().toLowerCase(),
        []
    );

    const [onlyMyDivision, setOnlyMyDivision] = useState(() => {
        if (!enableDivisionFilter) return false;
        const stored = localStorage.getItem(divisionFilterStorageKey);
        return stored === null ? true : stored === '1';
    });

    const handleDivisionFilterChange = useCallback((checked) => {
        setOnlyMyDivision(checked);
        localStorage.setItem(divisionFilterStorageKey, checked ? '1' : '0');
    }, [divisionFilterStorageKey]);

    useEffect(() => {
        if (!open) return;
        loadData();
    }, [open]);

    useEffect(() => {
        // Only run when modal opens and data is loaded
        if (!open) return;

        // Initialize selected from already selected, but ensure we have full data
        if (alreadySelected.length > 0 && (employees.length > 0 || temps.length > 0)) {
            const fullDataSelected = alreadySelected.map(person => {
                let match = null;

                if (person._type === 'employee') {
                    // Try matching by employeeId/ee/EE first (more reliable)
                    const empId = person.employeeId || person.ee || person.EE;
                    if (empId) {
                        match = employees.find(e =>
                            String(e.EE) === String(empId) ||
                            String(e.ee) === String(empId) ||
                            String(e.employeeId) === String(empId)
                        );
                    }
                    // Fall back to name matching if no ID match
                    if (!match) {
                        match = employees.find(e => e.FullName === person.FullName);
                    }
                } else if (person._type === 'temp') {
                    // Try matching by tempId first if it exists
                    const tempId = person.tempId || person.id;
                    if (tempId) {
                        match = temps.find(t =>
                            String(t.id) === String(tempId) ||
                            String(t.tempId) === String(tempId)
                        );
                    }
                    // Fall back to name matching
                    if (!match) {
                        match = temps.find(t => t.FullName === person.FullName);
                    }
                }

                return match || person;
            });
            setSelected(fullDataSelected);
        } else if (alreadySelected.length > 0) {
            setSelected([...alreadySelected]);
        }
        // Don't set empty array repeatedly - only initialize when there's data
    }, [open, alreadySelected, employees, temps]);

    async function loadData() {
        setLoading(true);
        try {
            const db = await initializeDB();
            const txE = db.transaction(EMPLOYEE_STORE, 'readonly');
            const eStore = txE.objectStore(EMPLOYEE_STORE);
            const allEmployees = await eStore.getAll();

            // Map employees - include ALL fields (handle both cases)
            const mappedEmployees = allEmployees.map(e => ({
                ...e,  // Include all original fields
                FullName: e.FullName || e.fullName || 'Unknown',
                Division: e.Division || e.division || 'No Division',
                JobClassification: e.JobClassification || e.jobClassification || '',
                Phone: e.Phone || e.phone || '',
                email: e.email || '',
                // Also include lowercase versions for compatibility
                fullName: e.FullName || e.fullName || 'Unknown',
                division: e.Division || e.division || 'No Division',
                jobClassification: e.JobClassification || e.jobClassification || '',
                phone: e.Phone || e.phone || '',
                _type: 'employee'
            }));

            setEmployees(mappedEmployees);

            // Only load temps if not employeesOnly mode
            if (!employeesOnly) {
                const txT = db.transaction(TEMPS_STORE, 'readonly');
                const tStore = txT.objectStore(TEMPS_STORE);
                const allTemps = await tStore.getAll();

                const mappedTemps = allTemps.map(t => ({
                    ...t,
                    FullName: t.FullName || t.fullName || 'Unknown',
                    Agency: t.Agency || t.agency || 'No Agency',
                    // Дивизион temp'а (Temps.Division); у части temp'ов его нет — пусто.
                    Division: String(t.Division ?? t.division ?? '').trim(),
                    JobClassification: t.JobClassification || t.jobClassification || '',
                    Phone: t.Phone || t.phone || '',
                    email: t.email || '',
                    fullName: t.FullName || t.fullName || 'Unknown',
                    agency: t.Agency || t.agency || 'No Agency',
                    division: String(t.Division ?? t.division ?? '').trim(),
                    jobClassification: t.JobClassification || t.jobClassification || '',
                    phone: t.Phone || t.phone || '',
                    _type: 'temp'
                }));

                setTemps(mappedTemps);
            } else {
                setTemps([]);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    }

    // Filter all people based on search and type
    const filteredPeople = useMemo(() => {
        let allPeople = [];

        let employeePool = employees;
        if (enableDivisionFilter && onlyMyDivision && userDivision) {
            employeePool = employees.filter(e =>
                (e.Division || e.division || '').trim().toLowerCase() === userDivision
            );
        }

        if (filterType === 'all' || filterType === 'employee') {
            allPeople = [...allPeople, ...employeePool];
        }

        // Temp'ы чужого дивизиона прячем, а temp'ов без дивизиона оставляем:
        // куда они относятся, неизвестно, и прораб иначе не найдёт своего человека.
        let tempPool = temps;
        if (enableDivisionFilter && onlyMyDivision && userDivision) {
            tempPool = temps.filter(t => {
                const d = (t.Division || '').trim().toLowerCase();
                return d === '' || d === userDivision;
            });
        }

        if (filterType === 'all' || filterType === 'temp') {
            allPeople = [...allPeople, ...tempPool];
        }

        if (deferredSearch) {
            const search = deferredSearch.toLowerCase();
            allPeople = allPeople.filter(person => {
                const name = person.FullName.toLowerCase();
                const divisionAgency = `${person.Division || ''} ${person._type === 'temp' ? person.Agency || '' : ''}`.toLowerCase();
                return name.includes(search) || divisionAgency.includes(search);
            });
        }

        if (sortOrder === 'asc' || sortOrder === 'desc') {
            const sign = sortOrder === 'asc' ? 1 : -1;
            allPeople = [...allPeople].sort((a, b) =>
                sign * (a.FullName || '').localeCompare(b.FullName || '', undefined, { sensitivity: 'base' })
            );
        } else if (sortOrder === 'division') {
            // Дивизион по возрастанию (10, 23, 35…), без дивизиона — в конец; внутри — по имени.
            const div = p => { const d = (p.Division || '').trim(); return d === '' || d === 'No Division' ? null : d; };
            allPeople = [...allPeople].sort((a, b) => {
                const da = div(a), db = div(b);
                if (da !== db) {
                    if (da === null) return 1;
                    if (db === null) return -1;
                    return da.localeCompare(db, undefined, { numeric: true });
                }
                return (a.FullName || '').localeCompare(b.FullName || '', undefined, { sensitivity: 'base' });
            });
        }

        if (pinSelfFirst) {
            const selfIdx = findSelfIndex(allPeople);
            if (selfIdx > 0) {
                allPeople = [allPeople[selfIdx], ...allPeople.slice(0, selfIdx), ...allPeople.slice(selfIdx + 1)];
            }
        }

        return allPeople;
    }, [employees, temps, deferredSearch, filterType, enableDivisionFilter, onlyMyDivision, userDivision, sortOrder, pinSelfFirst]);

    // Рисуем частями: сотни карточек MUI разом подвешивали открытие окна (10/2026).
    const { visible: visiblePeople, hasMore: morePeople, sentinelRef: peopleSentinel } = useProgressiveList(filteredPeople, {
        resetKey: `${open}|${deferredSearch}|${filterType}|${onlyMyDivision}|${sortOrder}`,
    });

    const selectedSet = useMemo(() => {
        const set = new Set();
        selected.forEach(s => {
            set.add(`${s._type}-${s.FullName}`);
        });
        return set;
    }, [selected]);

    const isSelected = useCallback((person) => {
        return selectedSet.has(`${person._type}-${person.FullName}`);
    }, [selectedSet]);

    const toggleSelection = useCallback((person) => {
        setSelected(prev => {
            if (singleSelect) {
                // Single select mode: replace selection
                const alreadySelected = prev.length > 0 && prev[0].FullName === person.FullName && prev[0]._type === person._type;
                return alreadySelected ? [] : [person];
            } else {
                // Multiple select mode: toggle
                const alreadySelected = prev.some(s =>
                    s.FullName === person.FullName && s._type === person._type
                );

                if (alreadySelected) {
                    return prev.filter(s =>
                        !(s.FullName === person.FullName && s._type === person._type)
                    );
                } else {
                    return [...prev, person];
                }
            }
        });
    }, [singleSelect]);

    const selectedSetRef = useRef(selectedSet);
    selectedSetRef.current = selectedSet;

    const clearSearch = useCallback(() => {
        setSearchTerm('');
        searchInputRef.current?.focus();
    }, []);

    // Отметили человека из результатов поиска — чистим поиск, чтобы сразу искать
    // следующего без стирания. Снятие отметки поиск не трогает.
    const handlePersonTap = useCallback((person) => {
        const adding = !selectedSetRef.current.has(`${person._type}-${person.FullName}`);
        toggleSelection(person);
        if (adding && searchTermRef.current) clearSearch();
    }, [toggleSelection, clearSearch]);

    function handleConfirm() {
        onSelect(selected);
        onClose();
    }

    function handleClose() {
        setSearchTerm('');
        setFilterType('all');
        onClose();
    }

    const counts = {
        all: employees.length + temps.length,
        employee: employees.length,
        temp: temps.length
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            // z-index выше стандартной модалки (1300) — чтобы пикер был поверх
            // родительского Dialog'а (напр. окно подписи таймшита), а не под ним.
            sx={{ zIndex: (theme) => theme.zIndex.modal + 50 }}
            PaperProps={{
                sx: { height: '90vh', maxHeight: 800 }
            }}
        >
            <DialogTitle sx={{ pb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Select Team Members</Typography>
                    <IconButton onClick={handleClose} edge="end">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={showSelectedStrip ? { p: 2, display: 'flex', flexDirection: 'column' } : { p: 2 }}>
                {/* Search Bar */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name or division/agency..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    inputRef={searchInputRef}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm ? (
                            <InputAdornment position="end">
                                <IconButton size="small" edge="end" aria-label="Clear search" onClick={clearSearch}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                {/* Filter Buttons - hide when employeesOnly */}
                {!employeesOnly && (
                    <Box sx={{ mb: 2 }}>
                        <ToggleButtonGroup
                            value={filterType}
                            exclusive
                            onChange={(e, newValue) => newValue && setFilterType(newValue)}
                            fullWidth
                            size="small"
                        >
                            <ToggleButton value="all">
                                All ({counts.all})
                            </ToggleButton>
                            <ToggleButton value="employee">
                                Employees ({counts.employee})
                            </ToggleButton>
                            <ToggleButton value="temp">
                                Temps ({counts.temp})
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {enableDivisionFilter && userDivision && (
                    <Box sx={{ mb: 1.5 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={onlyMyDivision}
                                    onChange={(e) => handleDivisionFilterChange(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    Only my division ({localStorage.getItem('employeeDivision') || ''})
                                </Typography>
                            }
                        />
                    </Box>
                )}

                {/* Selected Count. С полосой выбранных строка не нужна: она
                    появлялась после первого тапа и сдвигала список под пальцем. */}
                {!showSelectedStrip && selected.length > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary">
                            {selected.length} selected
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => setSelected([])}
                            sx={{ ml: 'auto' }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}

                {/* Полоса выбранных: всегда на месте и одной строкой — список
                    под ней не прыгает, пока отмечаешь людей. */}
                {showSelectedStrip && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexShrink: 0 }}>
                        <SelectedStrip
                            empty="Nobody selected yet — tap people below"
                            sx={{ flex: 1, minWidth: 0 }}
                        >
                            {selected.map(p => (
                                <Chip
                                    key={`${p._type}-${p.FullName}`}
                                    label={p.FullName}
                                    onDelete={() => toggleSelection(p)}
                                    sx={{
                                        flexShrink: 0,
                                        fontWeight: 600,
                                        bgcolor: p._type === 'temp' ? '#fff3e0' : '#e3f2fd',
                                        color: p._type === 'temp' ? '#e65100' : '#1565c0',
                                    }}
                                />
                            ))}
                        </SelectedStrip>
                        <Button
                            size="small"
                            onClick={() => setSelected([])}
                            disabled={selected.length === 0}
                            sx={{ flexShrink: 0 }}
                        >
                            Clear all
                        </Button>
                    </Box>
                )}

                {/* List header — Name + sort toggle (taps cycle default → A→Z → Z→A) */}
                {!loading && filteredPeople.length > 0 && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5, py: 0.75, mb: 1,
                        bgcolor: 'grey.100',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}>
                        <Box
                            component="button"
                            onClick={() => setSortOrder(prev =>
                                prev === 'asc' ? 'desc' : prev === 'desc' ? 'default' : 'asc'
                            )}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                border: 'none',
                                bgcolor: 'transparent',
                                cursor: 'pointer',
                                p: 0.25,
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                color: sortOrder === 'asc' || sortOrder === 'desc' ? 'primary.main' : 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                            }}
                            title="Tap to sort by name (A→Z, Z→A, default)"
                        >
                            Name
                            {(sortOrder === 'default' || sortOrder === 'division') && <SwapVertIcon sx={{ fontSize: 18, opacity: 0.5 }} />}
                            {sortOrder === 'asc' && <ArrowUpIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                            {sortOrder === 'desc' && <ArrowDownIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                            {(sortOrder === 'asc' || sortOrder === 'desc') && (
                                <Typography component="span" variant="caption" sx={{ ml: 0.25, color: 'primary.main', fontWeight: 700 }}>
                                    {sortOrder === 'asc' ? 'A → Z' : 'Z → A'}
                                </Typography>
                            )}
                        </Box>
                        <Box
                            component="button"
                            onClick={() => setSortOrder(prev => (prev === 'division' ? 'default' : 'division'))}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0.25,
                                fontSize: '0.85rem', fontWeight: 600,
                                color: sortOrder === 'division' ? 'primary.main' : 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                            }}
                            title="Tap to sort by division (people without a division go last)"
                        >
                            Division
                            {sortOrder === 'division'
                                ? <ArrowUpIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                : <SwapVertIcon sx={{ fontSize: 18, opacity: 0.5 }} />}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {filteredPeople.length} {filteredPeople.length === 1 ? 'person' : 'people'}
                        </Typography>
                    </Box>
                )}

                {/* People List. С полосой выбранных список добирает остаток окна
                    флексом: фиксированная высота уехала бы за низ. */}
                <Box sx={showSelectedStrip
                    ? { flex: 1, minHeight: 0, overflow: 'auto' }
                    : { height: 'calc(100% - 220px)', overflow: 'auto' }}>
                    {loading ? (
                        <Stack spacing={1}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <Skeleton key={i} variant="rectangular" height={72} />
                            ))}
                        </Stack>
                    ) : filteredPeople.length === 0 ? (
                        <Card sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No people found
                            </Typography>
                        </Card>
                    ) : (
                        <Stack spacing={1}>
                            {visiblePeople.map((person, idx) => (
                                <PersonCard
                                    // Стабильный ключ: с номером строки при каждом символе поиска
                                    // все карточки пересоздавались заново.
                                    key={person.id != null ? `${person._type}-${person.id}` : `${person._type}-${person.FullName}-${idx}`}
                                    person={person}
                                    isSelected={isSelected(person)}
                                    onToggle={handlePersonTap}
                                    singleSelect={singleSelect}
                                    hideTypeChip={employeesOnly}
                                />
                            ))}
                            {morePeople && <Box ref={peopleSentinel} aria-hidden="true" sx={{ height: 24 }} />}
                        </Stack>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={handleClose}>
                    Cancel
                </Button>
                <Badge badgeContent={selected.length} color="primary">
                    <Button 
                        variant="contained" 
                        onClick={handleConfirm}
                        disabled={selected.length === 0}
                    >
                        Confirm Selection
                    </Button>
                </Badge>
            </DialogActions>
        </Dialog>
    );
}